import { Component, OnInit, AfterViewChecked } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { SkipFinanceService } from '@app/configure/services/skip-finance.service';
import { ConfigureService } from '@app/configure/services/configure.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';

import { PartExchangeVehicle } from '@app/configure/models/part-exchange-vehicle.model';
import { ModalAction } from '@app/core/models/modal-action.model';

@Component({
	selector: 'cng-configure-part-exchange',
	templateUrl: './part-exchange.component.html',
	styleUrls: ['./part-exchange.component.less'],
})
export class ConfigurePartExchangeComponent implements OnInit, AfterViewChecked {
	current_step_key: string = 'part_exchange';
	step_mode: string = this.configure_service.getStepMode();
	enquiry_hash: string = this.configure_service.configure_data.meta.enquiry_hash;
	part_exchanges: PartExchangeVehicle[] = this.configure_service.configure_data.steps.part_exchange.data;
	has_part_exchange: boolean = false;
	pex_vehicle_state: string = null;
	pex_vehicle_to_remove: PartExchangeVehicle = null;
	next_button_disabled: boolean = false;
	valuation: any;
	finance_locked: boolean = this.configure_service.configure_data.meta.finance.locked;
	destroyed$: Subject<boolean> = new Subject<boolean>();

	constructor(
		private configure_service: ConfigureService,
		private toast_service: ToastService,
		private modal_service: ModalService,
		private skip_finance_service: SkipFinanceService
	) {
		if (!this.configure_service.isStepEnabled('part_exchange')) {
			this.configure_service.navigateToStep('purchase_vehicle');
		}
	}

	ngOnInit(): void {
		this.checkForPartExchange();
		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data: any) => {
			this.part_exchanges = configure_data.steps.part_exchange.data;
			this.finance_locked = configure_data.meta.finance.locked;
			this.checkForPartExchange();
		});
		this.configure_service.track('step.moved', { step: this.current_step_key }).subscribe();
		this.configure_service.trackJourney().subscribe();

		if (this.step_mode === 'part_exchange' && this.part_exchanges.length === 0) {
			this.addNewPartExchange();
		}
	}

	ngAfterViewChecked(): void {
		if (!this.has_part_exchange && this.part_exchanges.length !== 0) {
			setTimeout(() => {
				if (typeof this.part_exchanges[0] !== 'undefined') {
					this.pex_vehicle_state = this.part_exchanges[0].state;
				} else {
					this.pex_vehicle_state = null;
				}
			});
		}
	}

	checkForPartExchange(): void {
		this.has_part_exchange =
			this.part_exchanges.filter((part_exchange: PartExchangeVehicle) => {
				return part_exchange.vehicle !== null;
			}).length > 0;
	}

	addNewPartExchange(): void {
		if (this.finance_locked) {
			this.modal_service.show(['configure/finance-locked']);

			return;
		}

		this.part_exchanges.push({ state: 'a', registration: null, cap_codes: null, vehicle: null });
	}

	onShowValuation(valuation: any, part_exchange: any): void {
		this.modal_service.show(['configure/part-exchange-valuation'], {
			valuation: valuation,
			part_exchange: part_exchange,
		});
	}

	onRemove(p: any): void {
		if (this.finance_locked) {
			this.modal_service.show(['configure/finance-locked']);

			return;
		}

		this.pex_vehicle_to_remove = p;

		this.modal_service.showConfirm({
			name: 'part_exchange_remove_modal',
			title: 'Remove part exchange vehicle?',
			content: ['Are you sure you want to remove this part exchange vehicle?'],
			actions: [
				new ModalAction({
					id: 'remove',
					name: 'Remove',
					bias: 'negative',
					grow: false,
					showLoading: true,
					click: (action: ModalAction): any => {
						this.removePartExchange(p, action);
					},
				}),
			],
		});
	}

	removePartExchange(pex_vehicle: PartExchangeVehicle, action: ModalAction): void {
		if (pex_vehicle.state === 'a') {
			this.configure_service.configure_data.steps.part_exchange.data = [];
			this.configure_service.setData();
			this.pex_vehicle_to_remove = null;
			this.next_button_disabled = false;
			this.modal_service.close();
		} else {
			this.configure_service.deletePartExVehicle(false).subscribe((response1: boolean) => {
				this.configure_service.saveCustomiseReview(true).subscribe((response2: boolean) => {
					if (response1 && response2) {
						this.toast_service.success('Part Exchange Vehicle Removed');
						this.pex_vehicle_to_remove = null;
						action.showLoading = false;
						this.modal_service.close();

						if (this.step_mode === 'part_exchange') {
							this.addNewPartExchange();
						} else {
							if (typeof this.part_exchanges[0] !== 'undefined') {
								this.pex_vehicle_state = this.part_exchanges[0].state;
							} else {
								this.pex_vehicle_state = null;
							}
						}
					}
				});
			});
		}
	}

	hasFollowingSteps(): boolean {
		return (
			this.configure_service.isStepEnabled('customise_review') ||
			this.configure_service.isStepEnabled('finance_options') ||
			this.configure_service.isStepEnabled('payment')
		);
	}

	jumpTo(new_step_key: string): void {
		// this.skip_finance_service.onJumpSteps(new_step_key, (canProceed: boolean) => {
		// 	if (canProceed) {
		this.saveAndGoTo(new_step_key);
		// 	}
		// });
	}

	onSkipThisStep(): void {
		const current_step_index = this.configure_service.configure_data.steps_order.indexOf(this.current_step_key);
		this.jumpTo(this.configure_service.configure_data.steps_order[current_step_index + 1] || 'purchase_vehicle');
	}

	saveAndGoTo(new_step_key: string): void {
		if (this.next_button_disabled) {
			this.toast_service.error('Please complete form or remove part exchange');
		} else {
			this.configure_service.navigateToStep(new_step_key);
		}
	}
}
